import type React from 'react';
import { useHistory } from 'react-router-dom';

import { Button, Divider, Heading, Icon, Paragraph, Text, Upsell } from '@zillow/constellation-10';
import { IconCheckmarkCircleFilled, IconPiggyBankFilled } from '@zillow/constellation-icons';

import { Lease, Property } from '../../../../../shared/models';
import { useIsFetching, useIsMutating } from '@zg-rentals/react-query5';
import { usePropertyInfoQuery } from '../../../../utils/queries';
import { useLeasingUrlParams } from '../../../../hooks/useLeasingUrlParams';
import LeaseModal from '../../../../components/Containers/LeaseModal';
import { SetUpPaymentsLink } from '../../../../components/Widgets/SetUpPaymentsLink';

type Props = {
  lease: Lease;
  onClose: () => void;
  isOpen: boolean;
};

function SignedSelfUploadSigningCompleteModal({ lease, isOpen, onClose }: Props): JSX.Element | null {
  const history = useHistory();
  const { propertyId } = useLeasingUrlParams();
  const { data: propertyData } = usePropertyInfoQuery(propertyId, !!propertyId);
  const property = propertyData ? new Property({ ...propertyData, propertyId }) : null;
  const isFetching = useIsFetching() > 0;
  const isMutating = useIsMutating() > 0;

  function onGoToDashboard(): void {
    history.push(Lease.getDashboardPath());
  }

  function renderSecondaryBodyContent() {
    return (
      <>
        <Heading
          level={3}
          css={{
            textStyle:
              'responsive.body-lg-bold' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
            marginBottom: 'tighter',
          }}
        >
          Next step
        </Heading>
        <Upsell
          header={<Heading level={4}>Set up payments</Heading>}
          body={
            <Text>
              It’s fast and free to allow tenants to pay rent, deposits, and fees directly to your bank account.
            </Text>
          }
          media={
            <Icon size="lg" role="img">
              <IconPiggyBankFilled />
            </Icon>
          }
          action={
            <SetUpPaymentsLink disabled={isFetching || isMutating} lease={lease} property={property}>
              Go to payments
            </SetUpPaymentsLink>
          }
          tone="soft"
          elevated={false}
          closeable={false}
        />
      </>
    );
  }

  function renderBody(): React.ReactNode {
    return (
      <>
        <Icon size="lg" role="img" css={{ marginBottom: 'tight', color: 'icon.feedback.success.strong' }}>
          <IconCheckmarkCircleFilled />
        </Icon>
        <Heading
          level={2}
          css={{
            textStyle:
              'responsive.heading-xs' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
            marginBottom: 'tight',
          }}
        >
          Your lease is finalized
        </Heading>
        <Paragraph>
          The lease will become active on its start date. You can view it anytime from your dashboard.
        </Paragraph>
        <Divider css={{ marginTop: 'tight', marginBottom: 'tight' }} />
        {renderSecondaryBodyContent()}
      </>
    );
  }

  function renderFooter(): React.ReactNode {
    return (
      <Button tone="brand" emphasis="filled" onClick={onGoToDashboard}>
        Go to your dashboard
      </Button>
    );
  }

  if (!lease) {
    return null;
  }

  return (
    <LeaseModal
      header={<Heading level={1}>Lease finalized</Heading>}
      isOpen={isOpen}
      onClose={onClose}
      footer={renderFooter()}
      body={renderBody()}
    />
  );
}

export default SignedSelfUploadSigningCompleteModal;
